import React, { FC } from 'react';
import { Tooltip } from 'reactstrap';
import styled from 'styled-components';
import { BasicContainer, ContainerProps } from './Containers';

type StyledDialogProps = {
  $maxWidth?: string | number;
};

const StyledDialog = styled(Tooltip)<StyledDialogProps>`
  transition: none;
  opacity: 1;

  [x-placement] {
    opacity: 1;
  }

  [x-placement^='top'] {
    & .arrow::before {
      border-top-color: ${({ theme }) => theme.designSystem.borderColors.menu};
    }
  }

  [x-placement^='bottom'] {
    & .arrow::before {
      border-bottom-color: ${({ theme }) => theme.designSystem.borderColors.menu};
    }
  }

  [x-placement^='left'] {
    & .arrow::before {
      border-left-color: ${({ theme }) => theme.designSystem.borderColors.menu};
    }
  }

  [x-placement^='right'] {
    & .arrow::before {
      border-right-color: ${({ theme }) => theme.designSystem.borderColors.menu};
    }
  }

  .tooltip-inner {
    background: ${({ theme }) => theme.designSystem.bgColors.dialog};
    color: ${({ theme }) => theme.designSystem.textColors.default};
    border: 1px solid ${({ theme }) => theme.designSystem.borderColors.menu};
    max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}` : 'none')};
    padding: 0;
  }

  .tooltip.show {
    opacity: 1;
  }
`;

export type DialogProps = {
  /** unique id value of an HTML element for the Dialog to render attached to */
  target: string | HTMLElement | React.RefObject<HTMLElement>;
  /** whether the Dialog is rendered or hidden */
  isOpen: boolean;
  /** amount of padding to place inside the tooltip around the children, defaults = "4" (1rem) */
  padding?: ContainerProps['padding'];
  /** location where the dialog should be rendered */
  container?: string;
  /** placement of the dialog relative to the target element */
  placement?: string;
  /** maximum width of the dialog */
  maxWidth?: string;
  /** accessible label for the dialog */
  'aria-label'?: string;
  /** qa id for the dialog */
  'data-qa-id'?: string;
  /** test id for the dialog */
  'data-testid'?: string;
};

/**
 * Dialog - wrapper component for an interactive tooltip / popover
 * see Design System reference: https://www.figma.com/design/AiDAD1WpprgfbZI2XV6hU1/Casting-Networks-Design-System?node-id=392-45&node-type=frame&t=pjlXDJCYPZrV3ogR-0
 */
export const Dialog: FC<DialogProps> = ({
  children,
  target,
  isOpen,
  padding = 4,
  container = 'inline',
  placement = 'bottom',
  'aria-label': ariaLabel,
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  maxWidth,
}) => {
  if (!target || !children || !isOpen) {
    return null;
  }

  return (
    <StyledDialog
      container={container}
      placement={placement}
      target={target}
      isOpen
      data-qa-id={dataQaId}
      data-testid={dataTestId}
      delay={0}
      $maxWidth={maxWidth}
    >
      <BasicContainer role="dialog" aria-label={ariaLabel} padding={padding}>
        {children}
      </BasicContainer>
    </StyledDialog>
  );
};
